import { CompanyLogoField } from '@components/pages/company/components/company-company-profile-modal-logo-field.component';
import { PreviousGovernmentWorkSection } from '@components/pages/company/components/company-company-profile-modal-previous-government-work-section.component';
import { withForm } from '@components/ui/form/form';
import { FormDescription } from '@components/ui/form/shared.component';
import { cn } from '@lib/utils';
import { detailsCommercialSchema } from '@schemas/company.schema';
import { DEFAULT_COUNTRY, cmmcLevelOptions, countries, usStates } from '@utils/constants';
import { resetFieldMeta } from '@utils/form.util';
import { companyProfileModalDetailsAdditionalDefaultProps } from './company-company-profile-modal-details.types';
import { companyFormOptions } from './company-form-options';

const stateOptions = usStates.map((state) => ({
  label: `${state.name} (${state.code})`,
  value: state.code,
}));

export const DetailsCommercialSection = withForm({
  ...companyFormOptions,
  props: companyProfileModalDetailsAdditionalDefaultProps,
  render: function Render(props) {
    const {
      companyLogoError,
      companyLogoPreviewUrl,
      form,
      isCompanyLogoDeleteRequested,
      onCompanyLogoFileSelect,
      onCompanyLogoRemove,
      onCompanyLogoRestore,
      showCompanyLogo = true,
    } = props;

    return (
      <div className="flex w-full flex-col items-start gap-6">
        <div
          className={cn(
            'grid w-full grid-cols-1 gap-4 md:items-start',
            showCompanyLogo ? 'md:grid-cols-[10rem_minmax(0,1fr)]' : 'md:grid-cols-1',
          )}
        >
          {showCompanyLogo && (
            <CompanyLogoField
              companyLogoError={companyLogoError}
              companyLogoPreviewUrl={companyLogoPreviewUrl}
              isCompanyLogoDeleteRequested={isCompanyLogoDeleteRequested}
              onCompanyLogoFileSelect={onCompanyLogoFileSelect}
              onCompanyLogoRemove={onCompanyLogoRemove}
              onCompanyLogoRestore={onCompanyLogoRestore}
            />
          )}
          <div className={cn('grid w-full grid-cols-1 gap-4', showCompanyLogo && 'md:pt-7')}>
            <form.AppField
              name="name"
              validators={{
                onBlur: detailsCommercialSchema.shape.name,
              }}
            >
              {(field) => (
                <field.TextField
                  autoComplete="off"
                  data-1p-ignore
                  label="Legal Name"
                  required
                  type="text"
                />
              )}
            </form.AppField>
            <form.AppField
              name="yearFounded"
              validators={{
                onBlur: detailsCommercialSchema.shape.yearFounded,
              }}
            >
              {(field) => (
                <field.TextField
                  inputMode="numeric"
                  label="Year Founded"
                  placeholder={new Date().getFullYear().toString()}
                  type="text"
                />
              )}
            </form.AppField>
          </div>
        </div>

        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-3">
          <form.AppField
            listeners={{
              onChange: () => {
                const currentCountry = form.getFieldValue('country');
                if (currentCountry && currentCountry !== DEFAULT_COUNTRY) {
                  form.setFieldMeta('samsUeiNumber', resetFieldMeta);
                  form.setFieldValue('city', '');
                  form.setFieldValue('samsUeiNumber', undefined);
                  form.setFieldValue('state', '');
                }
              },
            }}
            name="country"
            validators={{
              onChange: detailsCommercialSchema.shape.country,
            }}
          >
            {(field) => (
              <field.SelectPopoverField
                className="w-full"
                emptyMessage="No country found"
                formatValue={(option) => option.label}
                label="Country"
                options={countries}
                placeholder="Select a country"
                searchPlaceholder="Search country..."
              />
            )}
          </form.AppField>

          <form.AppField
            name="state"
            validators={{
              onBlur: detailsCommercialSchema.shape.state,
            }}
          >
            {(field) => (
              <form.Subscribe selector={(state) => state.values.country}>
                {(country) =>
                  country === DEFAULT_COUNTRY ? (
                    <field.SelectField
                      className="w-full"
                      label="State"
                      options={stateOptions}
                      placeholder="Select a state"
                    />
                  ) : (
                    <field.TextField
                      label="Province"
                      placeholder="Enter state/province"
                      type="text"
                    />
                  )
                }
              </form.Subscribe>
            )}
          </form.AppField>

          <form.AppField
            name="city"
            validators={{
              onBlur: detailsCommercialSchema.shape.city,
            }}
          >
            {(field) => (
              <field.TextField
                autoComplete="off"
                data-1p-ignore
                label="City"
                type="text"
              />
            )}
          </form.AppField>
        </div>

        <form.AppField
          name="description"
          validators={{
            onBlur: detailsCommercialSchema.shape.description,
          }}
        >
          {(field) => (
            <field.TextAreaField
              label="Description"
              textareaClassName="flex h-28 w-full rounded-lg bg-gray-100 p-3"
            />
          )}
        </form.AppField>

        <form.AppField
          name="cmmcLevel"
          validators={{
            onChange: detailsCommercialSchema.shape.cmmcLevel,
          }}
        >
          {(field) => (
            <div className="flex w-full flex-col gap-1.5">
              <field.SelectField
                className="w-full"
                clearable
                label="CMMC Level"
                options={cmmcLevelOptions.map((option) => ({
                  label: option.label,
                  value: option.value,
                }))}
                placeholder="Select CMMC Level"
              />
              <FormDescription>
                The CMMC Program ensures DoD contractors protect sensitive unclassified information by meeting
                cybersecurity standards for nonfederal systems. Please confirm your level.
              </FormDescription>
            </div>
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.country}>
          {(country) =>
            country === DEFAULT_COUNTRY ? (
              <form.AppField
                name="samsUeiNumber"
                validators={{
                  onBlur: detailsCommercialSchema.shape.samsUeiNumber,
                }}
              >
                {(field) => (
                  <field.TextField
                    label="SAMs UEI Number"
                    type="text"
                  />
                )}
              </form.AppField>
            ) : null
          }
        </form.Subscribe>

        <PreviousGovernmentWorkSection form={form} />
      </div>
    );
  },
});
